import { Hit } from 'algoliasearch';
import type { AlgoliaNetwork } from 'components/CoverageSearch/searchTypes';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_INDEX_NAME_ASC,
  ALGOLIA_SEARCH_KEY,
  reorderRATs,
  transformRAT,
} from 'components/CoverageSearch/searchUtils';
import type { CoverageSearchProfiles } from 'components/CoverageSearch/useCoverageProfiles';
import { Workbook } from 'exceljs';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import { SimsDaoSimBilling, StructsBillingRateResponse } from 'types/__generated__/SimInventoryApi';
import { EnumSimInventoryExportType } from 'types/common';
import type { LegacyPlan } from 'types/legacyDashboard';
import downloadCSV from 'utils/files/downloadCSV';
import downloadXLSX from 'utils/files/downloadXLSX';
import parseUnc from 'utils/strings/parseUnc';

type ExportRow = {
  profile: string;
  networkName: string;
  ratePerMb: number;
  country: string;
  region: string;
  networkDesignation: string;
  mcc: string;
  mnc: string;
  tadig: string;
  supportedRats: string;
};

type AlgoliaResponse = {
  hits: Hit<AlgoliaNetwork>[];
  nbHits: number;
  page: number;
  nbPages: number;
  hitsPerPage: number;
};

interface ExportCoverageDataParams {
  filters: string;
  profiles: CoverageSearchProfiles[] | null;
  legacyPlan?: LegacyPlan;
  hologramSimId?: HologramSimId;
  billingRates: StructsBillingRateResponse;
  simBilling?: SimsDaoSimBilling;
  onSuccess?: (exportType: EnumSimInventoryExportType) => void;
  onError?: (error: Error) => void;
}

export default async function exportCoverageData({
  filters,
  profiles,
  legacyPlan,
  hologramSimId,
  billingRates,
  simBilling,
  onSuccess,
  onError,
}: ExportCoverageDataParams) {
  try {
    // This loop fetches all network coverage data from Algolia in batches of 1000 hits
    // Since Algolia paginates results, and the max we can fetch is 1000, we might need to make multiple requests to get all data
    // We track the current page and total pages, concatenating hits until we've fetched everything
    const url = `https://${ALGOLIA_APP_ID}.algolia.net/1/indexes/${ALGOLIA_INDEX_NAME_ASC}/query`;
    const headers = {
      'X-Algolia-Application-Id': ALGOLIA_APP_ID,
      'X-Algolia-API-Key': ALGOLIA_SEARCH_KEY,
      'Content-Type': 'application/json',
    };
    const body = { filters, hitsPerPage: 1000, distinct: false };

    let allHits: Hit<AlgoliaNetwork>[] = [];
    let currentPage = 0;
    let totalPages = 1; // Initialize to 1 to start the loop

    do {
      const pageBody = { ...body, page: currentPage };
      // eslint-disable-next-line no-await-in-loop
      const networksResponse = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(pageBody),
      });

      if (!networksResponse.ok) {
        throw new Error(`Error fetching results (page ${currentPage})`);
      }

      // eslint-disable-next-line no-await-in-loop
      const networksData: AlgoliaResponse = await networksResponse.json();

      allHits = allHits.concat(networksData.hits);
      totalPages = networksData.nbPages;
      currentPage += 1;
    } while (currentPage < totalPages);

    const rows: ExportRow[] = [];

    allHits.forEach((hit) => {
      const { code, offer_id: offerId, supported_rat: supportedRat, data_rate: dataRate } = hit;
      const parsedUnc = parseUnc(code);
      let ratePerMb =
        billingRates?.data?.find((rate) => rate.unc === code)?.costPerMb ?? Number(dataRate);
      if (billingRates.rateType === 'flat_rate') {
        if (simBilling?.dataRate) ratePerMb = simBilling.dataRate;
        else ratePerMb = legacyPlan?.overageCharge!;
      }
      rows.push({
        networkName: hit.name,
        profile: profiles?.find((p) => offerId.includes(p.offerId))?.profileName ?? 'Unknown',
        country: hit.country,
        region: parsedUnc?.region ?? '',
        networkDesignation: code,
        mcc: parsedUnc?.mcc ?? '',
        mnc: parsedUnc?.mnc ?? '',
        tadig: parsedUnc?.tadig ?? '',
        ratePerMb,
        supportedRats: reorderRATs(supportedRat)
          .map((rat) => transformRAT(rat))
          .join(', '),
      });
    });

    const now = DateTime.now();
    const timestamp = now.toFormat('yyyyLLddhhmmss');
    let filename: string;
    if (hologramSimId) {
      filename = `Coverage-SIM-ID-${hologramSimId}-Plan-ID-${simBilling?.planId}-${timestamp}`;
    } else if (legacyPlan) {
      filename = `Coverage-Plan-ID-${legacyPlan?.id}-${timestamp}`;
    }

    return async (exportType: EnumSimInventoryExportType) => {
      switch (exportType) {
        case EnumSimInventoryExportType.XLSX: {
          const workbook = new Workbook();
          workbook.creator = 'Hologram';
          workbook.lastModifiedBy = 'Hologram';

          const nowJS = now.toJSDate();
          workbook.created = nowJS;
          workbook.modified = nowJS;

          const sheet = workbook.addWorksheet('Coverage');
          sheet.addRow(Object.keys(rows[0]));
          sheet.addRows(rows.map((record) => Object.values(record)));

          const buffer = await workbook.xlsx.writeBuffer();

          downloadXLSX({
            data: buffer as any,
            filename: `${filename}.xlsx`,
          });
          break;
        }
        case EnumSimInventoryExportType.CSV:
        default:
          downloadCSV({
            data: unparse(rows),
            filename: `${filename}.csv`,
          });
      }

      onSuccess?.(exportType);
    };
  } catch (error) {
    onError?.(error as Error);
    throw error;
  }
}
